<template>
  <a ref="showHelpAnchor" v-if="display" @click.prevent="onHelpClick" @keydown.enter="onHelpClick" tabindex="0" role="button" :aria-label="`Open help on '${title}'`" class="help-link">
    <span class="visually-hidden-focusable">Click this link to open the help content</span>
    <template v-if="hasCustomIcon">
      <img :src="customIcon" style="width: 1.2rem;" :alt="`Learn more about ${title}`" />
    </template>
    <template v-else-if="props.linkText.length > 0">
      <span class="link-text">{{ linkText }}</span>
    </template>
    <template v-else>
      <i class="bi" :class="icon" :style="customStyle"></i>
    </template>
  </a>

  <modal
    ref="helpModal"
    :title="title"
    :close-button-aria-label="getCloseButtonAriaLabel()"
    :idKey="getTitleWithoutSpaces()"
    icon="bi-question-circle-fill"
    :isWide="isWide"
    :returnFocusTo="showHelpAnchor"
    :customIconOverride="customIcon"
  >
    <template v-for="(para, i) in text" v-bind:key="i">
      <p v-html="para"></p>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { Configuration } from "@/guiide/ts/configuration/configuration";
import { ref, computed } from "vue";
import { useStore } from "vuex";
import Modal from "./Modal.vue";

const props = defineProps({
  title: { type: String, required: false, default: "" },
  text: { type: Object as () => Array<string>, required: false },
  isWide: { type: Boolean, required: false, default: false },
  color: { type: String, default: "" },
  enabled: { type: Boolean, default: true },
  display: { type: Boolean, default: true },
  icon: { type: String, default: "bi-question-circle-fill", required: false },
  fontSize: { type: String, default: "", required: false },
  linkText: { type: String, default: "", required: false }
});

defineExpose({ takeFocus });

const store = useStore();
const showHelpAnchor = ref<HTMLElement>(null);
const helpModal = ref(null);

const customIcon = computed<string>(() => {
  return store.state.configuration?.customHelpIcon ?? "";
});

const hasCustomIcon = computed<boolean>(() => {
  return customIcon.value.length > 0;
});

const customStyle = computed<string>(() => {
  const selectedColor = props.color != null && props.color != ""
          ? props.color
          : Configuration.getCssVariableValue("--quaternary-color");

  let size = "";
  if (props.fontSize.length) {
    size = `font-size: ${props.fontSize};`
  }

  return `color: ${selectedColor};${size}`;
});

function getTitleWithoutSpaces() {
  return props.title.replace(/[^a-zA-Z0-9]+/g, "");
}

function getCloseButtonAriaLabel(): string {
  let helpText = "";

  for (const para of props.text) {
    const cleanedForAriaLabel = cleanHelpParaForAriaLabel(para);
    helpText += cleanedForAriaLabel + "\n";
  }

  return `Close button on "${props.title}" help popup, the help is: ${helpText}Press enter or space to close this help window.`;
}

function cleanHelpParaForAriaLabel(para: string): string {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = para;

  tempDiv.querySelectorAll("a").forEach((a) => {
    const href = a.getAttribute("href") || "";
    const linkText = href.trim();
    a.replaceWith(document.createTextNode(linkText));
  });

  tempDiv.querySelectorAll("strong").forEach((s) => {
    const capitalised = s.innerText?.toUpperCase() + ".\n";
    s.replaceWith(document.createTextNode(capitalised));
  });

  return tempDiv.textContent?.trim() ?? "";
}

function onHelpClick() {  
  if (props.enabled) {
    helpModal.value.showModal();
  }
}

function takeFocus(): boolean {
  if (!props.display) {
    return false;
  }
  showHelpAnchor.value.focus();
  return true;
}
</script>

<style scoped>
p {
  color: #666665;
  font-size: 15px;
  font-family: Ubuntu, "Helvetica Neue Light", "Helvetica Neue", Helvetica,
    "Lato", Arial, sans-serif;
  color: #666665;
  font-weight: 400;
  text-align: left !important;
  margin-bottom: 15px;
  line-height: 1.7;
  margin: 0 0 10px;
  display: block;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
}

.bi-question-circle-fill:hover {
  cursor: pointer;
}

.help-link {
  text-decoration: none;
  color: inherit;
}

.help-link .link-text {
  text-decoration: underline;
}
</style>
